import React from "react";
import { TextArea, TextField, Button } from "ui/components";
import { Subtitle } from "core/models";
import regexSubtitleTimecode from "core/constants/regexSubtitleTimecode";
import { cx } from "utils/join-classnames";
import $ from "./SubtitleCard.module.scss";

interface Props {
    subtitle: Subtitle;
    onChange(subtitle: Subtitle): void;
    onDelete(): void;
    onFocus(target: "startTime" | "endTime" | "text"): void;
    onBlur(): void;
    isActive: boolean;
    isHovered: boolean;
}

const alwaysTripleDigits = (n: number): string => {
    if (n < 10) {
        return `00${n}`;
    }
    if (n >= 10 && n < 100) {
        return `0${n}`;
    }
    if (n >= 1000) {
        return n.toString().slice(0, 3);
    }
    return n.toString();
};

const convertTimefloatToTimecode = (timeFloat: number): string => {
    const alwaysDoubleDigits = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    const minutes = Math.floor(timeFloat / 60);
    const seconds = Math.floor(timeFloat - minutes * 60);
    const ms = ((timeFloat - minutes * 60 - seconds) * 1000).toFixed(0);
    return `${alwaysDoubleDigits(minutes)}:${alwaysDoubleDigits(
        seconds,
    )}.${alwaysTripleDigits(+ms)}`;
};
const convertTimecodeToTimefloat = (timecode: string): number => {
    const [minutes, seconds, miliseconds] = timecode.split(/[:.]/);
    const timeFloat = +minutes * 60 + +seconds + +miliseconds / 1000;
    return timeFloat;
};

const SubtitleCard: React.FC<Props> = ({
    subtitle,
    onChange,
    onDelete,
    onFocus,
    onBlur,
    isActive = false,
    isHovered = false,
}) => {
    const [errors, setErrors] = React.useState<string[]>([]);
    const [timecodeStart, setTimecodeStart] = React.useState<string>(
        convertTimefloatToTimecode(subtitle.startTime),
    );
    const [timecodeEnd, setTimecodeEnd] = React.useState<string>(
        convertTimefloatToTimecode(subtitle.endTime),
    );

    const addError = (where: "start" | "end"): void => {
        const newErrors = [...errors];
        if (newErrors.find((err) => err === where)) {
            return;
        }
        newErrors.push(where);
        setErrors(newErrors);
    };
    const removeError = (where: "start" | "end"): void => {
        const newErrors = errors.filter((err) => err !== where);
        setErrors(newErrors);
    };
    const saveTimecode = (type: "start" | "end", timecode: string): void => {
        const re = new RegExp(regexSubtitleTimecode);
        if (!re.test(timecode)) {
            addError(type); // don't save invalid timecodes
        } else {
            removeError(type);
            const newSubtitle = { ...subtitle };
            newSubtitle[type === "start" ? "startTime" : "endTime"] =
                convertTimecodeToTimefloat(timecode);
            onChange(newSubtitle);
        }
    };
    const handleTimeUpdate = (
        what: "start" | "end",
        timecode: string,
    ): void => {
        const re = new RegExp("^([0-9]{0,2}):([0-9]{0,2})[.]([0-9]{0,3})$");
        if (!re.test(timecode)) {
            return;
        }
        if (what === "start") {
            setTimecodeStart(timecode);
        } else {
            setTimecodeEnd(timecode);
        }
        saveTimecode(what, timecode);
    };
    const handleTextUpdate = (newText: string): void => {
        const newSubtitle = { ...subtitle };
        newSubtitle.text = newText;
        onChange(newSubtitle);
    };

    React.useEffect(() => {
        setTimecodeStart(convertTimefloatToTimecode(subtitle.startTime));
        setTimecodeEnd(convertTimefloatToTimecode(subtitle.endTime));
    }, [subtitle]);

    return (
        <div
            className={cx(
                $.subtitleCard,
                isActive ? $.subtitleCard__active : null,
                isHovered ? $.subtitleCard__hovered : null,
            )}
            onBlur={onBlur}
        >
            <div className={$.subtitleCard__times}>
                <div
                    className={cx($.timecode, $.timecodeIn)}
                    onFocus={() => onFocus("startTime")}
                >
                    <TextField
                        onValueChanged={(value) =>
                            handleTimeUpdate("start", value)
                        }
                        theme="dark"
                        defaultText="00:00"
                        value={timecodeStart}
                        className={
                            errors.some((err) => err === "start") ||
                            subtitle.error
                                ? $.error
                                : undefined
                        }
                    />
                </div>
                <div
                    className={cx($.timecode, $.timecodeOut)}
                    onFocus={() => onFocus("endTime")}
                >
                    <TextField
                        onValueChanged={(value) =>
                            handleTimeUpdate("end", value)
                        }
                        theme="dark"
                        defaultText="00:00"
                        value={timecodeEnd}
                        className={
                            errors.some((err) => err === "end") ||
                            subtitle.error
                                ? $.error
                                : undefined
                        }
                    />
                </div>
            </div>
            <div
                className={$.subtitleCard__content}
                onFocus={() => onFocus("text")}
            >
                <TextArea
                    value={subtitle.text}
                    onValueChanged={handleTextUpdate}
                    className={$.textarea}
                />
            </div>
            <div className={$.subtitleCard__actions}>
                <Button type="function" icon="delete" onClick={onDelete} />
            </div>
        </div>
    );
};

export default SubtitleCard;
